'use client';

import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl'
import { KuikIcon } from '@/components/svg/icons/KuikIcon'
import Link from 'next/link';
import ProtectedEmail from '@/components/ui-elements/ProtectedEmail';
import { fadeInUp, viewportOnce } from '@/lib/animations';

export const Footer = () => {
  const t = useTranslations('Footer')
  const year = new Date().getFullYear();

  const badges = [
    'EMPSA member',
    'ISO 27001',
    'ISO 20022',
    'TIPS ready',
  ];

  return (
    <motion.footer
      className="w-full bg-dark text-white pt-12 pb-8 px-6 sm:px-10 lg:px-16"
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
      variants={fadeInUp}
    >
      <div className="max-w-8xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-gray-800">
          <div className="lg:col-span-6 flex flex-col space-y-5">
            <Link href={`/`} className="flex items-center gap-2 group w-fit">
              <div className="w-9 h-9 flex items-center justify-center">
                <KuikIcon width={36} height={36} fill="white" />
              </div>
              <span className="text-2xl font-black tracking-tight text-white flex items-start">
                kuik
                <span className="text-[10px] font-semibold text-white ml-0.5 mt-1">®</span>
              </span>
            </Link>

            <p className="text-sm text-gray-300 leading-relaxed max-w-sm">
              {t.rich('subtitle', {
                br: () => <br />,
              })}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {badges.map((badge) => (
                <span
                  key={badge}
                  className="bg-badge hover:bg-primary transition-colors text-gray-300 text-xs px-3 py-1.5 rounded-full border border-blue-900/40"
                >
                  {badge}
                </span>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {/* First Column: Products */}
            <div className="flex flex-col space-y-3">
              <h4 className="text-xs font-bold tracking-wider text-gray-400 uppercase">
                {t('products')}
              </h4>
              <ul className="space-y-2.5 text-sm font-medium">
                <li>
                  <Link href={`/merchants`} className="hover:color-primary transition-colors">
                    {t('for_merchants')}
                  </Link>
                </li>
                <li>
                  <Link href={`/banks`} className="hover:color-primary transition-colors">
                    {t('for_banks')}
                  </Link>
                </li>
              </ul>
            </div>
            <div className="flex flex-col space-y-3">
              <h4 className="text-xs font-bold tracking-wider text-gray-400 uppercase">
                {t('company')}
              </h4>
              <ul className="space-y-2.5 text-sm font-medium">
                <li>
                  <a
                    href="https://mpay.al"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:color-primary transition-colors"
                  >
                    {t('visit')} mpay.al
                  </a>
                </li>
                <li>
                  <Link href={`/privacy-policy`} className="hover:color-primary transition-colors">
                    {t('privacy_policy')}
                  </Link>
                </li>
                <li>
                  <Link href={`/general-terms-conditions`} className="hover:color-primary transition-colors">
                    {t('terms_use')}
                  </Link>
                </li>
                <li>
                  <Link href={`/website-terms-policy`} className="hover:color-primary transition-colors">
                    {t('web_terms_policy')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Contact */}
            <div className="flex flex-col space-y-3 col-span-2 sm:col-span-1">
              <h4 className="text-xs font-bold tracking-wider text-gray-400 uppercase">
                {t('contact')}
              </h4>
              <ProtectedEmail
                encodedEmail="aW5mb0BtcGF5LmFs"
                className="text-sm font-medium hover:color-primary transition-colors break-all"
              />
              <h4 className="text-xs font-medium tracking-wider text-gray-400 uppercase">
                kuik.me
              </h4>
            </div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400 font-medium">
          <p>© {year} Kuik by Mpay Albania. All rights reserved.</p>
          <p>
            Powered by{' '}
            <a
              href="https://mpay.al"
              target="_blank"
              rel="noopener noreferrer"
              className="color-primary hover:underline font-semibold"
            >
              Mpay Albania
            </a>
            {' '}- AL / EN
          </p>
        </div>

      </div>
    </motion.footer>
  );
}